<template>
  <div
    v-if="colourOptions.length > 1"
    class="colour-family-swatches"
    :class="{
      'colour-family-swatches--card': variant === 'card',
    }"
    data-testid="colour-family-swatches"
  >
    <a
      v-for="option in colourOptions"
      :key="option.id"
      :href="option.url"
      class="colour-family-swatches__swatch"
      :class="swatchClasses(option)"
      :style="option.imageUrl ? undefined : { backgroundColor: option.color }"
      :aria-label="option.label"
      :aria-current="option.isCurrent ? 'page' : undefined"
      @click.stop="handleSwatchClick($event, option)"
    >
      <img
        v-if="option.imageUrl"
        :src="option.imageUrl"
        alt=""
        class="colour-family-swatches__swatch-image"
      />
    </a>
  </div>
</template>

<script>
import {
  getColourFamilyProducts,
  getColourFamilyProductUrl,
  getColourFamilySwatchColor,
  getColourFamilySwatchImage,
} from "@/helpers/colourFamily.js";
import { mapState } from "vuex";

export default {
  name: "ColourFamilySwatches",
  props: {
    product: {
      type: Object,
      default: null,
    },
    variant: {
      type: String,
      default: "pdp",
      validator(value) {
        return ["pdp", "card"].includes(value);
      },
    },
  },
  computed: {
    ...mapState("product", { storeProduct: "product" }),
    activeProduct() {
      return this.product || this.storeProduct;
    },
    colourOptions() {
      return getColourFamilyProducts(this.activeProduct).map(
        (colourProduct) => {
          const imageUrl = getColourFamilySwatchImage(colourProduct);
          const color = getColourFamilySwatchColor(colourProduct);

          return {
            id: colourProduct.id,
            label: colourProduct.title,
            url: getColourFamilyProductUrl(colourProduct),
            imageUrl,
            color,
            isCurrent: this.activeProduct
              ? colourProduct.id === this.activeProduct.id
              : false,
          };
        }
      );
    },
  },
  methods: {
    swatchClasses(option) {
      return {
        "colour-family-swatches__swatch--active": option.isCurrent,
        "colour-family-swatches__swatch--card-active":
          this.variant === "card" && option.isCurrent,
        "colour-family-swatches__swatch--image": option.imageUrl,
      };
    },
    handleSwatchClick(event, option) {
      event.stopPropagation();

      if (!option.url) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      window.location.assign(option.url);
    },
  },
};
</script>

<style lang="scss" scoped>
.colour-family-swatches {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0 0;

  &--card {
    margin: 0;
    flex: 0 0 auto;
    gap: 4px;
    justify-content: flex-end;
    position: relative;
    z-index: 1;
  }

  &__swatch {
    display: block;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 1px solid rgba($color-black, 0.5);
    padding: 0;
    text-decoration: none;
    flex: 0 0 22px;
    box-sizing: border-box;
    overflow: hidden;

    &--active {
      border: 0.5px solid $color-black;
      cursor: pointer;
    }

    &--card-active {
      border: 1px solid $color-black;
      cursor: pointer;
    }

    &--image {
      background-color: transparent;
    }
  }

  &__swatch-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }

  a#{&}__swatch {
    cursor: pointer;

    &:hover {
      border: 1px solid $color-black;
    }
  }
}

.colour-family-swatches--card {
  .colour-family-swatches__swatch {
    width: 17px;
    height: 17px;
    flex: 0 0 17px;

    @include respond-below(md) {
      width: 15px;
      height: 15px;
      flex: 0 0 15px;
    }
  }

  @include respond-below(md) {
    justify-content: flex-start;
  }
}
</style>
